"use client";

import { motion } from "framer-motion";
import { useTranslation } from "../hooks/useTranslation";
import { useIsMobile } from "../hooks/useIsMobile";
import GlassCard from "./GlassCard";

export default function AboutSection() {
	const { t } = useTranslation();
	const isMobile = useIsMobile();

	const stats = [
		{
			label: t("about.experience"),
			value: t("about.years"),
			icon: "🎓",
		},
		{
			label: t("about.students"),
			value: t("about.studentsCount"),
			icon: "👥",
		},
	];

	return (
		<section className="py-10 px-4">
			<div className="max-w-6xl mx-auto">
				{isMobile ? (
					<div className="text-center mb-16">
						<h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
							{t("about.title")}
						</h2>
					</div>
				) : (
					<motion.div
						initial={{ opacity: 0, y: 50 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
						viewport={{ once: true }}
						className="text-center mb-16"
					>
						<h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
							{t("about.title")}
						</h2>
					</motion.div>
				)}

				<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
					{/* Left Column - Text Content */}
					{isMobile ? (
						<div className="space-y-6">
							<p className="text-lg text-gray-700 leading-relaxed mb-6">
								{t("about.description1")}
							</p>
							<p className="text-lg text-gray-700 leading-relaxed">
								{t("about.description2")}
							</p>
						</div>
					) : (
						<motion.div
							initial={{ opacity: 0, x: -50 }}
							whileInView={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.8 }}
							viewport={{ once: true }}
							className="space-y-6"
						>
							<p className="text-lg text-gray-700 leading-relaxed mb-6">
								{t("about.description1")}
							</p>
							<p className="text-lg text-gray-700 leading-relaxed">
								{t("about.description2")}
							</p>
						</motion.div>
					)}

					{/* Right Column - Stats Cards */}
					{isMobile ? (
						<div className="grid grid-cols-2 lg:grid-cols-1 gap-4 sm:gap-6 h-full">
							{stats.map((stat) => (
								<div key={stat.label} className="h-full">
									<div className="p-6 text-center flex flex-col items-center justify-center h-full min-h-[160px] w-full bg-gradient-to-r from-[#4ae6ff] to-[#fc9de3] border-2 border-[#4ae6ff] rounded-2xl shadow-xl text-white">
										<div className="text-4xl mb-3 flex-shrink-0">
											{stat.icon}
										</div>
										<div className="text-3xl font-bold text-white mb-2 flex-shrink-0">
											{stat.value}
										</div>
										<div className="text-white text-center leading-tight flex-grow flex items-center justify-center">
											{stat.label}
										</div>
									</div>
								</div>
							))}
						</div>
					) : (
						<motion.div
							initial={{ opacity: 0, x: 50 }}
							whileInView={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.8, delay: 0.2 }}
							viewport={{ once: true }}
							className="grid grid-cols-1 gap-4 sm:gap-6 items-stretch"
						>
							{stats.map((stat) => (
								<div key={stat.label}>
									<GlassCard
										className="p-6 text-center flex flex-col items-center justify-center min-h-[140px] w-full hover:scale-105 transition-transform duration-300"
										hover={true}
									>
										<div className="text-4xl mb-3">{stat.icon}</div>
										<div className="text-3xl font-bold text-gray-900 mb-2">
											{stat.value}
										</div>
										<div className="text-gray-700 text-center leading-tight">
											{stat.label}
										</div>
									</GlassCard>
								</div>
							))}
						</motion.div>
					)}
				</div>
			</div>
		</section>
	);
}
